import type { RouteObject } from 'react-router';
import { Navigate } from 'react-router';

import {
  BaseRouteType,
  IdParamsType,
  NestedRoutesType,
} from 'common/src/routes/BaseRoutes';
import { generateNestedRoute } from 'common/src/routes/helpers';

import { IAuthorizationContext } from 'portal/src/auth/IAuthorizationContext';
import {
  IHeaderTab,
  IPageProps,
} from 'portal/src/layouts/LayoutPreference/layoutTypes';
import Page from 'portal/src/layouts/Page';

type RouteProps = {
  children?: RouteProps[];
  element: React.ReactElement;
  index?: boolean;
  path?: NestedRoutesType | IdParamsType;
};

type MenuProps = {
  name: string;
  shouldRenderLink?: (context: IAuthorizationContext) => boolean;
  dividerBefore?: boolean;
};

export type RouteDefinition = RouteProps & MenuProps;

/**
 * Converts route definitions into header tab menu items for navigation display.
 * @param definitions - Array of route definitions containing name, path, and render conditions.
 * @param basePath - The base route under which all tab paths are nested.
 * @returns An array of `IHeaderTab` objects used to render the page header navigation.
 */
function mapTabDefinitionsToMenuItems(
  definitions: RouteDefinition[],
  basePath: BaseRouteType,
): IHeaderTab[] {
  return definitions.map((t) => ({
    name: t.name,
    path: generateNestedRoute(basePath, t.path as NestedRoutesType),
    shouldRender: t.shouldRenderLink,
    dividerBefore: t.dividerBefore,
  }));
}

/**
 * Recursively converts route prop definitions into React Router `RouteObject` entries.
 * @param definitions - Array of route props, each optionally containing nested children.
 * @returns An array of `RouteObject` entries, or `undefined` if the input is empty.
 */
function mapTabDefinitionsToRoutes(definitions: RouteProps[]): RouteObject[] {
  if (!definitions?.length) {
    return undefined;
  }

  return definitions.map(
    (t) =>
      ({
        path: t.path,
        element: t.element,
        children: mapTabDefinitionsToRoutes(t.children),
        index: t.index ? true : false,
      }) as RouteObject,
  );
}

/**
 * Generates a React Router `RouteObject` for a page that renders a tabbed layout.
 * Includes an index redirect to the default tab and child routes for each tab definition.
 * @param pageTitle - The heading text displayed on the page.
 * @param basePath - The top-level route path for this page.
 * @param defaultTab - The nested route to redirect to when the base path is matched exactly.
 * @param definitions - Array of route definitions describing each tab's path, element, and menu metadata.
 * @param PageElement - Optional layout component to render; defaults to `Page`.
 * @returns A `RouteObject` representing the full tabbed page route tree.
 */
export function generatePageWithTabsRoute(
  pageTitle: string,
  basePath: BaseRouteType,
  defaultTab: NestedRoutesType,
  definitions: RouteDefinition[],
  PageElement: React.ElementType<IPageProps> = Page,
): RouteObject {
  return {
    path: basePath,
    element: (
      <PageElement
        tabs={mapTabDefinitionsToMenuItems(definitions, basePath)}
        title={pageTitle}
      />
    ),
    children: [
      { index: true, element: <Navigate to={defaultTab} replace /> },
      ...mapTabDefinitionsToRoutes(definitions),
    ],
  };
}
